import { useApiMutation } from "@gladis/api-client";
import { Button, Label, MoneyInput, parseEurInput, SelectField, TextField } from "@gladis/ui";
import { errorSentence, useT } from "@gladis/ui/i18n";
import { useState, type FormEvent, type ReactNode } from "react";

import { PROPERTIES_KEY } from "./PropertyList.js";

const ENERGY_LABELS = ["A++", "A+", "A", "B", "C", "D", "E", "F", "G"] as const;

/**
 * Add a property with its opening valuation (FR-001 to FR-010).
 *
 * ============================================================================
 * THE OPENING VALUATION IS PRE-FILLED HERE, AND NOWHERE ELSE
 * ============================================================================
 *
 * The value follows the purchase price and the date follows the purchase date, until the member
 * edits either — at which point it stops following, because they have said what they want.
 *
 * The SERVER supplies no default (FR-009). That division is deliberate: a default resolved on the
 * server would hand a client that omitted the field a valuation nobody ever saw, which is worse than
 * a refusal because nothing would prompt anyone to question the number. Here the household sees the
 * figure before they save it.
 *
 * CLEARING IT BLOCKS THE SAVE. That is FR-008 at the point of entry — a property never exists
 * without a value — and the reason the rest of the module never has to handle a valuation-less
 * property.
 */
export function PropertyForm({ onCreated }: { onCreated?: () => void }): ReactNode {
  const t = useT();
  const [street, setStreet] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [houseAddition, setHouseAddition] = useState("");
  const [postcode, setPostcode] = useState("");
  const [city, setCity] = useState("");
  const [countryCode, setCountryCode] = useState("NL");
  const [propertyType, setPropertyType] = useState<"house" | "apartment">("house");
  const [buildYear, setBuildYear] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [energyLabel, setEnergyLabel] = useState<string>("");
  /*
   * OPTIONAL, AND MISSING UNTIL NOW.
   *
   * `CreatePropertyRequestSchema` has accepted both since 008, the domain stores them, the DTO
   * carries them and the detail page displays them — but no form ever offered them, so every
   * property in the database has null for both and there was no way to fix that. The gap only
   * became visible when the Details card started showing the fields.
   *
   * Left blank they stay null, which is a legitimate state: a household that does not know its plot
   * size should not be made to invent one.
   */
  const [innerSurfaceAreaM2, setInnerSurfaceAreaM2] = useState("");
  const [outerSurfaceAreaM2, setOuterSurfaceAreaM2] = useState("");

  /*
   * `null` means "still following the purchase fields". A string means the member has taken it over.
   *
   * A boolean `touched` flag plus a value would be two pieces of state that can disagree; one
   * nullable value cannot.
   */
  const [valuationOverride, setValuationOverride] = useState<string | null>(null);
  const [valuationDateOverride, setValuationDateOverride] = useState<string | null>(null);

  const valuation = valuationOverride ?? purchasePrice;
  const valuationDate = valuationDateOverride ?? purchaseDate;

  const create = useApiMutation<{ propertyId: string }, Record<string, unknown>>(
    (client, input) => client.post<{ propertyId: string }>("/properties", input),
    { invalidates: [PROPERTIES_KEY] },
  );

  // Required for NL only, matching the domain rule exactly. The server refuses again on submit with
  // `PROP_ENERGY_LABEL_REQUIRED`; this is the affordance, never the enforcement.
  const needsEnergyLabel = countryCode.trim().toUpperCase() === "NL";
  const valuationAmount = parseEurInput(valuation);
  const canSubmit =
    valuationAmount !== null &&
    valuationDate !== "" &&
    parseEurInput(purchasePrice) !== null &&
    (!needsEnergyLabel || energyLabel !== "");

  function submit(event: FormEvent): void {
    event.preventDefault();
    const price = parseEurInput(purchasePrice);
    if (price === null || valuationAmount === null) return;

    create.mutate(
      {
        address: {
          street,
          houseNumber,
          houseAddition: houseAddition.trim() === "" ? null : houseAddition.trim(),
          postcode,
          city,
          countryCode: countryCode.trim().toUpperCase(),
        },
        propertyType,
        buildYear: Number(buildYear),
        innerSurfaceAreaM2: innerSurfaceAreaM2.trim() === "" ? null : Number(innerSurfaceAreaM2),
        outerSurfaceAreaM2: outerSurfaceAreaM2.trim() === "" ? null : Number(outerSurfaceAreaM2),
        purchasePrice: price,
        purchaseDate,
        energyLabel: energyLabel === "" ? null : energyLabel,
        openingValuation: { value: valuationAmount, valuationDate },
      },
      { onSuccess: () => onCreated?.() },
    );
  }

  return (
    /*
     * NO CARD CHROME AND NO HEADING. Both are the dialog's.
     *
     * This is the third form in this module to have carried them, and the third time the symptom
     * was the same: a bordered panel inside a modal, two borders deep, with the modal's own title
     * repeated underneath it. A form does not know where it is rendered and must not decide that it
     * is a card — the caller does, and the one caller that renders one inline supplies its own.
     */
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Label htmlFor="property-street">{t("prop.form.street")}</Label>
          <TextField
            id="property-street"
            value={street}
            onChange={(event) => setStreet(event.target.value)}
            placeholder={t("prop.form.streetPlaceholder")}
            required
          />
        </div>

        <div>
          <Label htmlFor="property-number">{t("prop.form.houseNumber")}</Label>
          <TextField
            id="property-number"
            value={houseNumber}
            onChange={(event) => setHouseNumber(event.target.value)}
            required
          />
        </div>

        <div>
          <Label htmlFor="property-addition">{t("prop.form.addition")}</Label>
          <TextField
            id="property-addition"
            value={houseAddition}
            onChange={(event) => setHouseAddition(event.target.value)}
            placeholder="B"
          />
        </div>

        <div>
          <Label htmlFor="property-postcode">{t("prop.form.postcode")}</Label>
          <TextField
            id="property-postcode"
            value={postcode}
            onChange={(event) => setPostcode(event.target.value)}
            required
          />
        </div>

        <div>
          <Label htmlFor="property-city">{t("prop.form.city")}</Label>
          <TextField
            id="property-city"
            value={city}
            onChange={(event) => setCity(event.target.value)}
            required
          />
        </div>

        <div>
          <Label htmlFor="property-country">{t("prop.form.country")}</Label>
          <TextField
            id="property-country"
            value={countryCode}
            onChange={(event) => setCountryCode(event.target.value)}
            maxLength={2}
          />
        </div>

        <div>
          <Label htmlFor="property-type">{t("prop.facts.type")}</Label>
          <SelectField
            id="property-type"
            value={propertyType}
            onChange={(event) => setPropertyType(event.target.value as "house" | "apartment")}
          >
            <option value="house">{t("prop.facts.house")}</option>
            <option value="apartment">{t("prop.facts.apartment")}</option>
          </SelectField>
        </div>

        <div>
          <Label htmlFor="property-build-year">{t("prop.form.buildYear")}</Label>
          <TextField
            id="property-build-year"
            value={buildYear}
            onChange={(event) => setBuildYear(event.target.value)}
            inputMode="numeric"
            required
          />
        </div>

        <div>
          <Label htmlFor="property-inner-area">{t("prop.form.floorArea")}</Label>
          <TextField
            id="property-inner-area"
            value={innerSurfaceAreaM2}
            onChange={(event) => setInnerSurfaceAreaM2(event.target.value)}
            inputMode="numeric"
          />
        </div>

        <div>
          <Label htmlFor="property-outer-area">{t("prop.form.plot")}</Label>
          <TextField
            id="property-outer-area"
            value={outerSurfaceAreaM2}
            onChange={(event) => setOuterSurfaceAreaM2(event.target.value)}
            inputMode="numeric"
          />
        </div>

        <div>
          <Label htmlFor="property-energy-label">
            Energy label{needsEnergyLabel ? "" : " (optional)"}
          </Label>
          <SelectField
            id="property-energy-label"
            value={energyLabel}
            onChange={(event) => setEnergyLabel(event.target.value)}
          >
            <option value="">—</option>
            {ENERGY_LABELS.map((label) => (
              <option key={label} value={label}>
                {label}
              </option>
            ))}
          </SelectField>
          {needsEnergyLabel && energyLabel === "" ? (
            <p className="mt-1 text-sm text-danger">{t("prop.form.energyLabelRequired")}</p>
          ) : null}
        </div>

        <div>
          <Label htmlFor="property-purchase-price">{t("prop.form.purchasePrice")}</Label>
          <MoneyInput
            id="property-purchase-price"
            value={purchasePrice}
            onValueChange={setPurchasePrice}
            required
          />
        </div>

        <div>
          <Label htmlFor="property-purchase-date">{t("prop.form.purchaseDate")}</Label>
          <TextField
            id="property-purchase-date"
            type="date"
            value={purchaseDate}
            onChange={(event) => setPurchaseDate(event.target.value)}
            required
          />
        </div>
      </div>

      {/*
       * A BORDERLESS GROUP. The `<fieldset>` stays — it is what associates the legend with these two
       * inputs for a screen reader, and that association is the point. Its BORDER does not: inside a
       * dialog it was a third rectangle nested in the second, and the visual grouping is already
       * carried by the legend and the spacing.
       */}
      <fieldset className="border-line border-t pt-4">
        <legend className="sr-only">{t("prop.form.worthToday")}</legend>
        <p className="text-sm font-medium">{t("prop.form.worthToday")}</p>
        <p className="text-muted mt-1 text-sm">{t("prop.form.worthTodayHint")}</p>

        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="property-valuation">{t("prop.list.colValue")}</Label>
            <MoneyInput
              id="property-valuation"
              value={valuation}
              onValueChange={setValuationOverride}
              required
            />
            {valuationAmount === null ? (
              <p className="mt-1 text-sm text-danger">{t("prop.form.valueRequired")}</p>
            ) : null}
          </div>

          <div>
            <Label htmlFor="property-valuation-date">{t("prop.form.asOf")}</Label>
            <TextField
              id="property-valuation-date"
              type="date"
              value={valuationDate}
              onChange={(event) => setValuationDateOverride(event.target.value)}
              required
            />
          </div>
        </div>
      </fieldset>

      <div className="mt-6 flex items-center gap-3">
        <Button type="submit" variant="primary" disabled={!canSubmit || create.isPending}>
          {create.isPending ? t("common.saving") : t("prop.form.addHeading")}
        </Button>
        {create.isError ? (
          <span className="text-sm text-danger">{errorSentence(create.error)}</span>
        ) : null}
      </div>
    </form>
  );
}
